// Station commands: the catalogue, the default expiries, and `command.updated`
// (spec/09-realtime-protocol.md §5.1, §6.2, §6.3; INFRA-18, #379).
//
// A command is a `station_command` row before it is a frame. The api writes the row (`queued`),
// publishes it on `station:{id}`, and the node holding the socket sends it (`sent`); the station
// answers `ack` within 10 s (`acked`, or `failed` with its error). Unacknowledged, it is sent again on
// the next connect — unless it has expired, and every type has a default expiry so a power command
// does not shut a PC down hours later. The station de-duplicates by id and keeps the last 500.
//
// The payload of each command is the feature's that sends it (F-SES-*, F-STN-*, F-SUP-*); this file
// holds what the delivery needs — the names, and how long each may wait.
import { z } from "../zod.js";
import { StationCommandIdSchema } from "../ids/schema.js";
import { COMMAND_UPDATED } from "./constants.js";
import { realtimeMessage } from "./envelope.js";
/**
 * Every server → station command in spec/09 §6.2, with its default expiry in seconds. `null` is
 * "until the hold ends": the issuer must give the expiry, because only it knows when that is.
 *
 * `station.state` is not here: it is the answer to `station.hello`, sent once per connect, never
 * stored and never re-sent (spec/09 §5.3).
 */
export const STATION_COMMAND_EXPIRY_SECONDS = {
    'session.start': 60,
    'session.lease': 24 * 60 * 60,
    'session.lock': 60,
    'session.unlock': 60,
    'session.pause': 60,
    'session.resume': 60,
    'session.end': 24 * 60 * 60,
    'session.message': 10 * 60,
    // A low-time warning at 10/5/1 minutes before the funded end (F-SES-11, spec/05 §5). Worthless once
    // the moment it warns about has passed, so it expires quickly — a station that reconnects reads its
    // real time left from the session state, not a stale warning.
    'session.warning': 2 * 60,
    // The session's funded time ran out and it is in grace (F-KIO-13, spec/05 §4.5): `{ sessionId, graceEndsAt }`,
    // so the kiosk takes over with the countdown and the ways to add time. Worthless once grace is over; a station
    // that reconnects reads the session's status (`grace`) from `station.state` instead.
    'session.grace': 5 * 60,
    // The live kitchen tracker for an order placed from this PC (F-KIO-11, spec/12 §7.6): sent whenever the
    // order's tickets move, and read again on reconnect so the tracker survives it (it is `station.*`, not
    // `session.*`, so `commandsToResend` re-sends it). Two hours is longer than any order takes to reach the
    // PC; a `null` order clears the tracker.
    'station.food.order': 2 * 60 * 60,
    // A kiosk pass purchase moved (F-KIO-14, spec/12 §7.1): applied once staff took payment or the UPI capture
    // landed, cancelled, or expired. Re-sent on reconnect (it is `station.*`) so a PC that was offline still
    // learns what became of the player's purchase; two hours outlives any pending desk order or UPI request.
    'station.pass.purchase': 2 * 60 * 60,
    // A kiosk food order's payment moved (F-KIO-10, spec/12 §7.5): paid at the desk or by the UPI capture, or
    // cancelled by staff. Re-sent on reconnect (it is `station.*`); two hours outlives any unpaid food order.
    'station.food.checkout': 2 * 60 * 60,
    // A wallet top-up asked for at this PC moved (F-KIO-10, spec/12 §7.1): applied once staff took the money at the
    // desk (or the UPI capture landed), cancelled, or expired. Forwarded to the kiosk as `wallet/topUp`; once applied
    // the station re-reads the wallet and the Account. Re-sent on reconnect (it is `station.*`); two hours outlives any
    // request left waiting at the desk.
    'station.wallet.topup': 2 * 60 * 60,
    // The session player's wallet moved at the desk (2026-10-02): a sale refunded to the wallet, or a counter top-up.
    // `{}` — "re-read the wallet now" (`GET /v1/station/sessions/current/wallet`), so the kiosk's balance follows without
    // waiting for a reconnect. Every connect in session pulls it anyway, so ten minutes is plenty.
    'station.wallet.update': 10 * 60,
    'station.mode.set': 60,
    // Run the lockdown self-test and report each check (F-STN-21, spec/13 §12): `{ testId }`. Staff are waiting on the
    // answer, so five minutes; a station that was offline longer gets a fresh request.
    'station.lockdown.test': 5 * 60,
    'station.power': 60,
    'station.wol.send': 60,
    'station.policy.update': 24 * 60 * 60,
    'station.library.update': 24 * 60 * 60,
    // Rescan what is installed now and report it (F-CAT-03, spec/10 §7.2): staff asked from the console. Worth
    // little after a few minutes — every connect rescans anyway.
    'station.library.rescan': 5 * 60,
    // The venue's menu or its kiosk ordering changed (F-KIO-11, spec/12 §7.2): `{}` — "pull the menu now", so the
    // kiosk's Food screen follows the console (a dish sold out, a price, `fnb.kioskOrdering`) without a reload. Every
    // connect pulls the menu anyway, so an hour is plenty.
    'station.menu.update': 60 * 60,
    'station.title.update': 6 * 60 * 60,
    'station.process.kill': 60,
    'station.app.launch': 60,
    'station.screen.start': 60,
    'station.screen.stop': 60,
    'station.webrtc.signal': 60,
    'station.fs.list': 60,
    'station.diagnostics.collect': 60 * 60,
    // "Update now" (F-STN-12, spec/10 §8.2; 2026-10-07): `{ reason: 'install_now' }` (`StationClientUpdateCommandPayload`) —
    // check for this station's release at once and install it as soon as no session runs. The console sends it only to
    // an online station; a day's expiry is the delivery grace for one that drops off between the click and the frame
    // (every reconnect checks anyway). Re-sent on reconnect (it is `station.*`).
    'station.client.update': 24 * 60 * 60,
    // "Skip setup" (F-STN-26, ADR-0055; 2026-10-11): `{ by, startedAt }` (`StationSetupSkipCommandPayload`) — end the PC's
    // start-up setup and open it for play now. Sent only to an online PC that is `setting_up`; worthless once that setup is
    // over (it ends on its own within its time limit), so five minutes. Re-sent on reconnect (it is `station.*`), and the
    // station ignores one for a setup other than the one staff saw.
    'station.setup.skip': 5 * 60,
    // "Install" on one PC / "Install on PCs missing it" (F-CAT-09, ADR-0056; 2026-10-11): `{ entryId, by, requestedAt }`
    // (`StationAppInstallCommandPayload`) — install this library app into the account players use as soon as the PC is free.
    // A request made while the PC is offline waits for it: the command is re-sent on every connect until acked (it is
    // `station.*`), for as long as the request itself lives — seven days (`APP_INSTALL_REQUEST_DAYS`), after which the
    // station drops a request it holds on the same clock. Staff ask again after that.
    'station.app.install': 7 * 24 * 60 * 60,
    'station.qr.rotate': 90,
    'station.reservation.hold': null,
    'station.reservation.release': null,
    'station.waitlist.hold': null,
    'station.waitlist.release': null,
    'station.call.updated': 20 * 60,
    // A sign-in handoff this station made was confirmed or refused, on the player's phone or in the PC's own sign-in window
    // (ADR-0051, F-KIO-20): `{ handoffId }` (`StationSignInHandoffReadyPayload`) — "collect it now", so the kiosk does not
    // have to poll every two seconds. Worthless after the handoff's three minutes and its minute's grace to be collected.
    'station.handoff.ready': 4 * 60,
    'station.print': 5 * 60,
    'station.drawer.open': 30,
    // Unpair or remove a station (ADR-0057; 2026-10-12): `{ by, reason }` (`StationUnpairCommandPayload`) — the PC forgets
    // its identity and shows a fresh pairing code. Sent while the PC is connected; its device key is revoked at once, so a
    // PC offline at the time cannot authenticate to take it later and the command simply expires.
    'station.unpair': 24 * 60 * 60,
};
export const STATION_COMMAND_TYPES = Object.keys(STATION_COMMAND_EXPIRY_SECONDS);
export const StationCommandTypeSchema = z
    .enum(STATION_COMMAND_TYPES)
    .openapi('StationCommandType', { description: 'A server → station command (spec/09 §6.2).' });
export function isStationCommandType(type) {
    return Object.hasOwn(STATION_COMMAND_EXPIRY_SECONDS, type);
}
/**
 * Commands about sessions are not replayed blindly (spec/09 §5.3): on reconnect the station is told
 * the authoritative session in `station.state` and reconciles, so a `session.*` command still pending
 * is not sent again — the state already says what it would have.
 */
export function isSessionCommand(type) {
    return type.startsWith('session.');
}
/** `queued` → `sent` → `acked` | `failed` | `expired` (spec/09 §5.1). */
export const STATION_COMMAND_STATUSES = ['queued', 'sent', 'acked', 'failed', 'expired'];
export const StationCommandStatusSchema = z.enum(STATION_COMMAND_STATUSES).openapi('StationCommandStatus', {
    description: 'Where a station command is: queued, sent, acked, failed or expired (spec/09 §5.1).',
});
/** A command that can still be delivered. */
export const PENDING_COMMAND_STATUSES = ['queued', 'sent'];
// ---------------------------------------------------------------------------------------------
// `command.updated` on `member:{id}` (spec/09 §6.3)
// ---------------------------------------------------------------------------------------------
export { COMMAND_UPDATED };
export const CommandUpdatedPayloadSchema = z
    .object({
    commandId: StationCommandIdSchema,
    status: StationCommandStatusSchema,
    /** What the station reported: its `result` when acked, its `error` when failed; `null` otherwise. */
    result: z.looseObject({}).nullable(),
})
    .openapi('CommandUpdatedPayload', {
    description: 'A command this member issued changed status: "Restarting… done" (spec/09 §5.1).',
});
export const CommandUpdatedMessage = realtimeMessage('evt', COMMAND_UPDATED, CommandUpdatedPayloadSchema);
//# sourceMappingURL=commands.js.map